import type { FC, FormEvent, ReactNode } from 'react'
import type { Subject } from '@/types'

import { useMemo, useState } from 'react'
import { cn } from '@/lib/utils'
import { compareText, formatDecimal } from '@/lib/academic'
import { Seo } from '@/components/generals/seo'
import { Badge } from '@/components/ui/badge'
import { Button, ButtonLink } from '@/components/ui/button'
import { Card, CardHeader } from '@/components/ui/card'
import { EmptyState } from '@/components/ui/feedback'
import { Field } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { PageHeader } from '@/components/app/page-header'
import { SearchField } from '@/components/app/search-field'
import { useAcademic } from '@/components/providers/academic-provider'
import { useConfirm } from '@/components/providers/confirm-provider'
import { useToast } from '@/components/providers/toast-provider'

import {
    THead,
    TBody,
    TD,
    TH,
    TR,
    Table
} from '@/components/ui/table'

import {
    ArrowRight,
    BookOpen,
    Layers,
    Pencil,
    Plus,
    SearchX,
    Trash2
} from 'lucide-react'

const parseKkm = (value: string): number | null => {
    if (value.trim() === '') return null

    const parsed = Number(value.replace(',', '.'))

    return Number.isFinite(parsed) ? parsed : Number.NaN
}

type SubjectRowProps = {
    subject: Subject
    index: number
}

const SubjectRow: FC<SubjectRowProps> = ({ subject, index }): ReactNode => {
    const { actions } = useAcademic()
    const confirm = useConfirm()
    const toast = useToast()
    const [editing, setEditing] = useState(false)
    const [name, setName] = useState(subject.name)
    const [kkm, setKkm] = useState(String(subject.kkm))

    const kkmLabel = formatDecimal(subject.kkm, 0)

    const handleSave = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
        event.preventDefault()

        const approved = await confirm({
            title: 'Simpan perubahan mata pelajaran?',
            description: `Nama dan KKM ${subject.name} akan diperbarui.`,
            confirmLabel: 'Simpan perubahan',
            tone: 'default'
        })

        if (!approved) return

        const result = actions.saveSubject({ id: subject.id, name, kkm: parseKkm(kkm) })

        if (result.ok) {
            toast.success(result.message ?? 'Mata pelajaran berhasil disimpan.')
            setEditing(false)
        }
        else {
            toast.error('Gagal menyimpan', result.error)
        }
    }

    const handleDelete = async (): Promise<void> => {
        const approved = await confirm({
            title: 'Hapus mata pelajaran beserta nilai terkait?',
            description: `${subject.name} dan seluruh nilai yang tercatat untuk mapel ini akan dihapus permanen.`,
            confirmLabel: 'Ya, hapus'
        })

        if (!approved) return

        const result = actions.deleteSubject(subject.id)

        if (result.ok) toast.success(result.message ?? 'Mata pelajaran dihapus.')
    }

    return (
        <li className='flex flex-col gap-4 px-5 py-4 transition-colors hover:bg-muted/30 sm:flex-row sm:items-center'>
            <span
                className='grid size-9 shrink-0 place-items-center rounded-lg bg-muted text-[13px] font-bold text-muted-foreground'
                aria-label={`Urutan ${index + 1}`}
            >
                {index + 1}
            </span>

            {editing ? (
                <form onSubmit={handleSave} className='flex min-w-0 flex-1 flex-col gap-2.5 sm:flex-row sm:items-center'>
                    <Input
                        value={name}
                        onChange={event => setName(event.target.value)}
                        maxLength={100}
                        required
                        aria-label={`Ubah nama ${subject.name}`}
                        className='h-9 sm:flex-1'
                        autoFocus
                    />

                    <div className='relative sm:w-28'>
                        <Input
                            type='number'
                            step='0.1'
                            min='0'
                            max='100'
                            value={kkm}
                            onChange={event => setKkm(event.target.value)}
                            aria-label={`KKM ${subject.name}`}
                            title='Ubah nilai KKM'
                            className='h-9 pr-12 text-center'
                        />

                        <span className='pointer-events-none absolute top-1/2 right-3 -translate-y-1/2 text-xs text-muted-foreground'>
                            / 100
                        </span>
                    </div>

                    <div className='flex gap-2'>
                        <Button type='submit' size='sm'>
                            Simpan
                        </Button>

                        <Button
                            size='sm'
                            variant='ghost'
                            onClick={() => {
                                setName(subject.name)
                                setKkm(String(subject.kkm))
                                setEditing(false)
                            }}
                        >
                            Batal
                        </Button>
                    </div>
                </form>
            ) : (
                <>
                    <div className='min-w-0 flex-1'>
                        <p className='truncate text-[15px] font-semibold text-foreground'>
                            {subject.name}
                        </p>

                        <p className='mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground'>
                            Standar KKM:
                            <Badge tone='primary'>{kkmLabel}</Badge>
                            Remedial jika &lt; {kkmLabel}
                        </p>
                    </div>

                    <div className='flex flex-wrap items-center gap-2'>
                        <ButtonLink href={`/nilai?mapel=${subject.id}`} variant='outline' size='sm' title='Input nilai untuk mapel ini'>
                            Input Nilai
                            <ArrowRight aria-hidden='true' />
                        </ButtonLink>

                        <Button variant='ghost' size='sm' onClick={() => setEditing(true)}>
                            <Pencil aria-hidden='true' />
                            Ubah
                        </Button>

                        <Button variant='danger-outline' size='sm' onClick={() => void handleDelete()}>
                            <Trash2 aria-hidden='true' />
                            Hapus
                        </Button>
                    </div>
                </>
            )}
        </li>
    )
}

const SubjectsPage: FC = (): ReactNode => {
    const { subjects, classes, users, classSubjectsSorted, actions } = useSubjectsData()
    const confirm = useConfirm()
    const toast = useToast()

    const [name, setName] = useState('')
    const [kkm, setKkm] = useState('')
    const [query, setQuery] = useState('')
    const [assignClass, setAssignClass] = useState('')
    const [assignSubject, setAssignSubject] = useState('')
    const [assignTeacher, setAssignTeacher] = useState('')

    const filtered = subjects.filter(subject => subject.name.toLocaleLowerCase('id').includes(query.trim().toLocaleLowerCase('id')))

    const handleCreate = (event: FormEvent<HTMLFormElement>): void => {
        event.preventDefault()

        const result = actions.saveSubject({ name, kkm: parseKkm(kkm) })

        if (result.ok) {
            toast.success(result.message ?? 'Mata pelajaran berhasil disimpan.')
            setName('')
            setKkm('')
        }
        else {
            toast.error('Gagal menyimpan', result.error)
        }
    }

    const handleAssign = (event: FormEvent<HTMLFormElement>): void => {
        event.preventDefault()

        const result = actions.assignSubject({
            classId: assignClass || classes[0]?.id || '',
            subjectId: assignSubject || subjects[0]?.id || '',
            teacherId: assignTeacher || null
        })

        if (result.ok) toast.success(result.message ?? 'Mapel berhasil di-assign ke kelas.')
        else toast.error('Gagal menyimpan', result.error)
    }

    const handleUnassign = async (id: string): Promise<void> => {
        const approved = await confirm({ title: 'Hapus assign?', description: 'Mata pelajaran tidak lagi tercatat untuk kelas ini.', confirmLabel: 'Ya, hapus' })

        if (!approved) return

        const result = actions.unassignSubject(id)

        if (result.ok) toast.success(result.message ?? 'Assign mapel dihapus.')
    }

    return (
        <>
            <Seo title='Mata pelajaran' />

            <PageHeader
                eyebrow='Data akademik'
                title='Kelola Mata Pelajaran'
                description='Kelola kurikulum mata pelajaran serta standar KKM (ambang batas remedial) untuk penilaian siswa.'
                actions={(
                    <div className='flex items-center gap-3 rounded-xl border bg-card px-4 py-2.5 shadow-card'>
                        <span className='grid size-9 place-items-center rounded-lg bg-primary/10 text-primary' aria-hidden='true'>
                            <BookOpen className='size-[18px]' />
                        </span>

                        <span>
                            <strong className='block text-xl leading-tight font-bold text-foreground'>{subjects.length}</strong>
                            <span className='text-xs text-muted-foreground'>Mapel terdaftar</span>
                        </span>
                    </div>
                )}
            />

            <div className='grid items-start gap-6 lg:grid-cols-[minmax(0,22rem)_minmax(0,1fr)]'>
                <Card>
                    <CardHeader
                        icon={<Plus />}
                        eyebrow='Tambah mapel'
                        title='Buat Mata Pelajaran'
                        description={<>Tentukan nama mapel dan nilai KKM acuan (opsional, otomatis <strong className='font-semibold text-foreground'>75</strong>).</>}
                    />

                    <form onSubmit={handleCreate} className='space-y-4 p-5'>
                        <Field label='Nama Mata Pelajaran' htmlFor='subjectName'>
                            <Input
                                id='subjectName'
                                value={name}
                                onChange={event => setName(event.target.value)}
                                maxLength={100}
                                placeholder='Contoh: Pemrograman Berorientasi Objek'
                                required
                            />
                        </Field>

                        <Field
                            label='Ambang Batas KKM (Remedial)'
                            htmlFor='subjectKkm'
                            hint={<>Siswa dengan nilai di bawah KKM akan ditandai <em>Remedial</em>.</>}
                        >
                            <div className='relative'>
                                <Input
                                    id='subjectKkm'
                                    type='number'
                                    step='0.1'
                                    min='0'
                                    max='100'
                                    value={kkm}
                                    onChange={event => setKkm(event.target.value)}
                                    placeholder='Default: 75'
                                    className='pr-14'
                                />

                                <span className='pointer-events-none absolute top-1/2 right-3 -translate-y-1/2 text-[13px] text-muted-foreground'>
                                    / 100
                                </span>
                            </div>
                        </Field>

                        <Button type='submit' className='w-full'>
                            <Plus aria-hidden='true' />
                            Tambahkan Mapel
                        </Button>
                    </form>
                </Card>

                <Card>
                    <CardHeader
                        eyebrow='Daftar mapel'
                        title='Mata Pelajaran Terdaftar'
                        action={(
                            <SearchField
                                label='Cari mata pelajaran'
                                placeholder='Cari mapel...'
                                value={query}
                                onChange={event => setQuery(event.target.value)}
                                wrapperClassName='w-full sm:w-60'
                            />
                        )}
                    />

                    {subjects.length === 0 ? (
                        <EmptyState
                            icon={<BookOpen />}
                            title='Belum Ada Mata Pelajaran'
                            description='Tambahkan mata pelajaran pertama menggunakan formulir di sebelah kiri.'
                        />
                    ) : filtered.length === 0 ? (
                        <EmptyState
                            icon={<SearchX />}
                            title='Tidak ditemukan'
                            description='Tidak ada mata pelajaran yang cocok dengan kata kunci pencarian.'
                        />
                    ) : (
                        <ul className='divide-y'>
                            {filtered.map(subject => (
                                <SubjectRow key={subject.id} subject={subject} index={subjects.indexOf(subject)} />
                            ))}
                        </ul>
                    )}
                </Card>
            </div>

            <Card className='mt-6'>
                <CardHeader
                    icon={<Layers />}
                    title='Assign Mapel ke Kelas'
                    description='Hubungkan mata pelajaran dengan kelas dan guru pengampu.'
                />

                <form onSubmit={handleAssign} className='grid gap-4 p-5 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_auto] md:items-end'>
                    <Field label='Kelas' htmlFor='assignClass'>
                        <Select id='assignClass' value={assignClass || classes[0]?.id || ''} onChange={event => setAssignClass(event.target.value)} required>
                            {classes.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}
                        </Select>
                    </Field>

                    <Field label='Mapel' htmlFor='assignSubject'>
                        <Select id='assignSubject' value={assignSubject || subjects[0]?.id || ''} onChange={event => setAssignSubject(event.target.value)} required>
                            {subjects.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}
                        </Select>
                    </Field>

                    <Field label='Guru' htmlFor='assignTeacher' optional>
                        <Select id='assignTeacher' value={assignTeacher} onChange={event => setAssignTeacher(event.target.value)}>
                            <option value=''>—</option>
                            {users.filter(user => user.active).map(user => <option key={user.id} value={user.id}>{user.name}</option>)}
                        </Select>
                    </Field>

                    <Button type='submit' className='md:w-24' disabled={classes.length === 0 || subjects.length === 0}>
                        OK
                    </Button>
                </form>

                {classSubjectsSorted.length > 0 && (
                    <div className='border-t'>
                        <Table>
                            <THead>
                                <tr>
                                    <TH>Kelas</TH>
                                    <TH>Mapel</TH>
                                    <TH>Guru</TH>
                                    <TH className='text-right'>
                                        <span className='sr-only'>Aksi</span>
                                    </TH>
                                </tr>
                            </THead>

                            <TBody>
                                {classSubjectsSorted.map(item => (
                                    <TR key={item.id}>
                                        <TD className='font-semibold text-foreground'>{item.className}</TD>
                                        <TD>{item.subjectName}</TD>
                                        <TD className={cn(!item.teacherName && 'text-muted-foreground')}>{item.teacherName ?? '—'}</TD>
                                        <TD className='text-right'>
                                            <Button variant='danger-outline' size='xs' onClick={() => void handleUnassign(item.id)}>
                                                <Trash2 aria-hidden='true' />
                                                Hapus
                                            </Button>
                                        </TD>
                                    </TR>
                                ))}
                            </TBody>
                        </Table>
                    </div>
                )}
            </Card>
        </>
    )
}

// Joins the class-subject assignments with their names, ordered like the original list
const useSubjectsData = () => {
    const { data, subjects, classes, users, classById, subjectById, userById, actions } = useAcademic()

    const classSubjectsSorted = useMemo(() => {
        return data.classSubjects
            .map(item => ({
                id: item.id,
                className: classById.get(item.classId)?.name ?? '-',
                subjectName: subjectById.get(item.subjectId)?.name ?? '-',
                teacherName: item.teacherId ? userById.get(item.teacherId)?.name ?? null : null
            }))
            .sort((a, b) => compareText(a.className, b.className) || compareText(a.subjectName, b.subjectName))
    }, [data.classSubjects, classById, subjectById, userById])

    return { subjects, classes, users, classSubjectsSorted, actions }
}

export default SubjectsPage
